import { useCallback } from "react";

import { v4 as uuidv4 } from "uuid";

import { AUDIO_TIMEOUT_MS, ENDPOINT_PROETHICS_TURN } from "../constants";
import { SCENARIO_REGISTRY } from "../data/scenario-registry";
import { registerTurnAbort, releaseTurnAbort } from "../helpers/audioAbort";
import { resolveHint } from "../helpers/hint";
import { createSSEParser } from "../helpers/parseUIMessageStream";
import { streamProethicsPost } from "../helpers/proethicsApi";
import { type TurnCtx, reduceTurnEvent } from "../helpers/turnReducer";
import type { TranscriptMessage, TurnEvent, TurnRequest } from "../types";
import { useProethicsFlowStore } from "./useProethicsFlowStore";

// Module-level (not per-hook-instance) so it survives the sim card unmounting on
// pause/navigation: identifies the in-flight turn so a slow trailing audio phase
// from an earlier turn can't clear `isStreaming` on a newer one after a remount.
let activeTurnId = 0;

export const useProethicsConversation = () => {
	const transcript = useProethicsFlowStore((s) => s.transcript);
	const arcStage = useProethicsFlowStore((s) => s.arcStage);
	const isStreaming = useProethicsFlowStore((s) => s.isStreaming);

	const sendText = useCallback(async (raw: string) => {
		const store = useProethicsFlowStore;
		const text = raw.trim();
		if (!text || store.getState().isStreaming) {
			return;
		}
		store.setState({ isStreaming: true });
		activeTurnId += 1;
		const turnId = activeTurnId;

		// Every write below is scoped to this scenarioId: Previous isn't gated on
		// `isStreaming`, so the learner can switch scenarios mid-turn, and a bare
		// `setState` would land those bytes on the wrong run.
		const scenarioId = store.getState().scenarioId;
		const avatarRole = SCENARIO_REGISTRY[scenarioId].avatarRole;
		const patchRun = store.getState().patchRun;

		// Mentor bubble appears immediately (we already have the text); the hint
		// pill hides until this turn's data-hint arrives (or the stream ends).
		const mentorMsg: TranscriptMessage = { id: uuidv4(), role: "mentor", text };
		const priorTranscript = store.getState().transcript;
		patchRun(scenarioId, (run) => ({
			transcript: [...run.transcript, mentorMsg],
			hintPending: true,
		}));

		const avatarId = uuidv4();
		const readRun = () => {
			const s = store.getState();
			return s.scenarioId === scenarioId
				? s
				: (s.runs[scenarioId] ?? { ...s, transcript: [] });
		};
		const ctx = (): TurnCtx => ({
			avatarId,
			avatarRole,
			isCurrentTurn: activeTurnId === turnId,
			isActiveScenario: store.getState().scenarioId === scenarioId,
		});

		const body: TurnRequest = {
			scenarioId,
			arcStage: store.getState().arcStage,
			transcript: priorTranscript.map(({ role, text: t }) => ({
				role,
				text: t,
			})),
			learnerInput: { modality: "text", text },
			previousHint:
				resolveHint(store.getState().currentHint, scenarioId) || undefined,
			threadId: store.getState().threadId ?? undefined,
		};

		const { signal, abort } = registerTurnAbort(turnId);

		let audioTimer: ReturnType<typeof setTimeout> | undefined;
		const clearAudioTimer = () => {
			if (audioTimer) {
				clearTimeout(audioTimer);
				audioTimer = undefined;
			}
		};

		const dispatch = (event: TurnEvent) => {
			const result = reduceTurnEvent(readRun(), event, ctx());
			if (Object.keys(result.patch).length > 0) {
				patchRun(scenarioId, result.patch);
			}
			if (result.clearAudioTimer) {
				clearAudioTimer();
			}
			if (result.startAudioTimer && !audioTimer) {
				audioTimer = setTimeout(() => {
					// Kill the request first so the server stops synthesizing and never
					// uploads; then collapse the bubble to `unavailable`.
					abort();
					dispatch({ type: "audio-timeout" });
				}, AUDIO_TIMEOUT_MS);
			}
		};

		// Audio arrives as ordered base64 chunks; assembled on `done` because the
		// expo-av player needs a complete data URI.
		const audioChunks: string[] = [];
		const feed = createSSEParser((part) => {
			if (part.type === "data-avatar-audio-chunk") {
				audioChunks.push(part.data.chunkBase64);
				return;
			}
			if (part.type === "data-avatar-audio-done") {
				const audioBase64 = audioChunks.join("");
				audioChunks.length = 0;
				dispatch({ type: "audio-assembled", data: { audioBase64 } });
				return;
			}
			dispatch(part);
		});

		try {
			await streamProethicsPost(ENDPOINT_PROETHICS_TURN, body, feed, signal);
			dispatch({ type: "stream-closed" });
		} catch {
			// Surface an error bubble only if the reply never arrived. An abort or
			// transport error during the trailing audio phase must not clobber a
			// reply that already streamed in.
			const hasReply = readRun().transcript.find(
				(m) => m.id === avatarId,
			)?.text;
			if (hasReply) {
				dispatch({ type: "audio-aborted" });
			} else {
				patchRun(scenarioId, (run) => ({
					transcript: [
						...run.transcript,
						{
							id: avatarId,
							role: avatarRole,
							text: "Sorry - something went wrong. Please try again.",
						},
					],
					...(activeTurnId === turnId ? { hintPending: false } : {}),
				}));
			}
		} finally {
			clearAudioTimer();
			releaseTurnAbort(turnId);
			// Clear only if a newer turn hasn't started: the audio phase can outlast
			// the interactive part of this turn (already cleared on turn-state).
			if (activeTurnId === turnId) {
				patchRun(scenarioId, { isStreaming: false });
			}
		}
	}, []);

	return { transcript, arcStage, isStreaming, sendText };
};

export type UseProethicsConversation = ReturnType<
	typeof useProethicsConversation
>;
